import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminLayout from '../components/AdminLayout'
import AdminStatsRow from '../components/AdminStatsRow'
import { AdminTable, Column } from '../components/AdminTable'
import { Filter } from 'lucide-react'
import adminService from '../utils/adminService'
import { ApiError } from '../utils/api'

type UserRow = {
  key: string
  name: string
  email: string
  phone: string
  filings: number
  companies: number
  dateRegistered: string
  status: string
}

type StatusFilter = 'all' | 'active' | 'inactive'

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All users' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

export default function AdminUsersManagementPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<UserRow[]>([])
  const [stats, setStats] = useState({ total: 0, active: 0 })
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [filterOpen, setFilterOpen] = useState(false)
  const filterRef = useRef<HTMLDivElement>(null)
  const itemsPerPage = 10

  // Debounce the search box so we fetch on pause, not per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400)
    return () => clearTimeout(t)
  }, [search])

  // Any new search / filter starts back at page 1.
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, statusFilter])

  // Close the filter menu on outside click.
  useEffect(() => {
    if (!filterOpen) return
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setFilterOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [filterOpen])

  useEffect(() => {
    const loadUsers = async () => {
      setError(null)
      setLoading(true)
      try {
        const { response } = await adminService.listUsers({
          page: currentPage,
          limit: itemsPerPage,
          ...(debouncedSearch ? { q: debouncedSearch } : {}),
          ...(statusFilter === 'active' ? { isActive: true } : statusFilter === 'inactive' ? { isActive: false } : {}),
        })
        const data: any = (response as any)?.data || response
        const items = data?.items || data?.data?.items || data?.users || data || []
        setRows(
          items.map((user: any, idx: number) => ({
            key: user.id || user._id || `${idx}`,
            name: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || '—',
            email: user.email || '—',
            phone: user.phoneNumber || user.phone || '—',
            filings: user.noOfFilings ?? user.filingsCount ?? 0,
            companies: user.registeredCompanies ?? user.companiesCount ?? 0,
            dateRegistered: user.createdAt
              ? new Date(user.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              : '—',
            status: user.isActive ? 'Active' : 'Inactive',
          }))
        )
        setTotalItems(data?.total || 0)
        // Stat cards show global counts (all users / all active), independent of
        // the current search, filter and page.
        setStats({
          total: data?.stats?.total ?? data?.total ?? 0,
          active: data?.stats?.active ?? items.filter((u: any) => u.isActive).length,
        })
      } catch (err) {
        console.error(err)
        setError(err instanceof ApiError ? err.message : 'Unable to load users.')
      } finally {
        setLoading(false)
      }
    }

    void loadUsers()
  }, [currentPage, debouncedSearch, statusFilter])

  const columns: Column<UserRow>[] = [
    { key: 'name', label: 'Full Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone Number' },
    { key: 'filings', label: 'No. Of Filings', render: row => <span className='text-[#ff4f3c] font-semibold'>{row.filings}</span> },
    { key: 'companies', label: 'Registered Companies', render: row => <span className='text-[#ff4f3c] font-semibold'>{row.companies}</span> },
    { key: 'dateRegistered', label: 'Date Registered' },
    {
      key: 'status',
      label: 'Status',
      render: row => (
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${
            row.status === 'Active'
              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
              : 'bg-red-50 text-red-700 border-red-100'
          }`}
        >
          {row.status}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: row => (
        <button
          onClick={() => navigate(`/admin/users/${row.key}`)}
          className='inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50'
        >
          View
        </button>
      ),
    },
  ]

  return (
    <AdminLayout>
      <div className='px-4 py-4 sm:px-6 sm:py-6 lg:px-10 space-y-5 sm:space-y-6'>
        <div>
          <h1 className='text-xl sm:text-2xl font-bold text-gray-900'>User Management</h1>
          <p className='text-sm text-gray-500'>Manage everyone registered on BritFiler.</p>
        </div>

        <AdminStatsRow
          stats={[
            { label: 'Total Users', value: stats.total },
            { label: 'Active Users', value: stats.active },
          ]}
        />

        {error && (
          <div className='rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700'>
            {error}
          </div>
        )}

        <AdminTable<UserRow>
          columns={columns}
          data={rows}
          loading={loading}
          emptyMessage='No users found.'
          searchPlaceholder='Search Users'
          searchValue={search}
          onSearchChange={setSearch}
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          actions={
            <div className='relative' ref={filterRef}>
              <button
                onClick={() => setFilterOpen(o => !o)}
                className='inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50'
              >
                <Filter className='h-4 w-4 text-gray-500' />
                Filter
                {statusFilter !== 'all' && (
                  <span className='ml-1 rounded-full bg-[#ff4f3c] px-2 py-0.5 text-xs font-semibold text-white'>
                    {statusFilter === 'active' ? 'Active' : 'Inactive'}
                  </span>
                )}
              </button>
              {filterOpen && (
                <div className='absolute right-0 z-20 mt-2 w-44 rounded-xl border border-gray-200 bg-white p-1 shadow-lg'>
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setStatusFilter(opt.value)
                        setFilterOpen(false)
                      }}
                      className={`w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                        statusFilter === opt.value ? 'bg-gray-50 font-semibold text-gray-900' : 'text-gray-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          }
        />
      </div>
    </AdminLayout>
  )
}
